import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import DataTable from '../common/DataTable';
import StatusBadge from '../common/StatusBadge';
import Alert from '../common/Alert';
import verificationService from '../../services/verificationService';
import useAuth from '../../hooks/useAuth';
import { CHECK_TYPES, VERIFICATION_STATUSES } from '../../utils/constants';
import './VerificationDashboard.css';

const messageFrom = (err, fallback) => err.response?.data?.message || err.message || fallback;

const VerificationDashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('MY_QUEUE');
  const [typeFilter, setTypeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  const [queueTasks, setQueueTasks] = useState(null);
  const [unassignedTasks, setUnassignedTasks] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState(null);
  const [bulkAssigning, setBulkAssigning] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const filters = {};
      if (statusFilter) filters.status = statusFilter;
      if (typeFilter) filters.check_type = typeFilter;

      const [queue, unassigned] = await Promise.all([
        verificationService.getQueue(filters),
        verificationService.getUnassigned(typeFilter ? { check_type: typeFilter } : {}),
      ]);
      setQueueTasks(queue.data || []);
      setUnassignedTasks(unassigned.data || []);
    } catch (err) {
      setError(messageFrom(err, 'Verification tasks could not be loaded.'));
    } finally {
      setLoading(false);
    }
  }, [statusFilter, typeFilter]);

  useEffect(() => { load(); }, [load]);

  const tasksForTab = () => {
    if (activeTab === 'MY_QUEUE') return queueTasks || [];
    if (activeTab === 'UNASSIGNED') return unassignedTasks || [];
    // ALL: no org-wide "all tasks" endpoint exists on the backend, so combine what's visible to this user.
    const seen = new Set();
    return [...(queueTasks || []), ...(unassignedTasks || [])].filter((task) => {
      if (seen.has(task.id)) return false;
      seen.add(task.id);
      return true;
    });
  };

  const filteredTasks = tasksForTab().filter((task) => {
    if (typeFilter && task.check_type !== typeFilter) return false;
    if (statusFilter && task.status !== statusFilter) return false;
    return true;
  });

  const stats = {
    assignedToMe: (queueTasks || []).length,
    attentionRequired: [...(queueTasks || []), ...(unassignedTasks || [])]
      .filter((t) => t.status === VERIFICATION_STATUSES.ATTENTION_REQUIRED).length,
    pending: (unassignedTasks || []).length,
    completedToday: (queueTasks || []).filter((t) => {
      if (!t.completed_at) return false;
      const completed = new Date(t.completed_at);
      const today = new Date();
      return completed.toDateString() === today.toDateString();
    }).length,
  };

  const ageDays = (task) => {
    const created = task.created_at ? new Date(task.created_at) : null;
    if (!created) return '—';
    return Math.max(0, Math.floor((Date.now() - created.getTime()) / (1000 * 60 * 60 * 24)));
  };

  const bulkAssignToMe = async () => {
    setBulkAssigning(true);
    setNotice(null);
    try {
      await Promise.all((unassignedTasks || []).map((task) => verificationService.assignTask(task.id, user.id)));
      setNotice({ type: 'success', message: 'Unassigned tasks assigned to you.' });
      await load();
    } catch (err) {
      setNotice({ type: 'error', message: messageFrom(err, 'Bulk assign failed.') });
    } finally {
      setBulkAssigning(false);
    }
  };

  const columns = [
    { header: 'App ID', accessor: 'application_request_id' },
    {
      header: 'Applicant',
      accessor: 'applicant_name',
      render: (row) => (
        <span
          className="task-link"
          onClick={() => navigate(`/verification/${row.id}`)}
          role="button"
          tabIndex={0}
        >
          {row.applicant_name || 'Applicant'}
        </span>
      )
    },
    { header: 'Type', accessor: 'check_type', render: (row) => <span className="type-label">{row.check_type.replace('_', ' ')}</span> },
    { header: 'Status', accessor: 'status', render: (row) => <StatusBadge status={row.status} /> },
    { header: 'Age (Days)', accessor: 'created_at', render: (row) => ageDays(row) }
  ];

  return (
    <div className="verification-dashboard">
      <div className="verification-header">
        <h1>Verification Hub</h1>
      </div>

      {error && <Alert type="error" message={error} />}
      {notice && <Alert type={notice.type} message={notice.message} dismissible onClose={() => setNotice(null)} />}

      <div className="verification-stats">
        <div className="stat-card">
          <h3>My Queue</h3>
          <p className="stat-value">{stats.assignedToMe}</p>
        </div>
        <div className="stat-card">
          <h3>Attention Required</h3>
          <p className="stat-value warning">{stats.attentionRequired}</p>
        </div>
        <div className="stat-card">
          <h3>Unassigned Pending</h3>
          <p className="stat-value">{stats.pending}</p>
        </div>
        <div className="stat-card">
          <h3>Completed Today</h3>
          <p className="stat-value success">{stats.completedToday}</p>
        </div>
      </div>

      <div className="card dashboard-tasks">
        <div className="card-header dashboard-tasks__header">
          <div className="tabs">
            <button className={`tab ${activeTab === 'MY_QUEUE' ? 'active' : ''}`} onClick={() => setActiveTab('MY_QUEUE')}>My Queue</button>
            <button className={`tab ${activeTab === 'UNASSIGNED' ? 'active' : ''}`} onClick={() => setActiveTab('UNASSIGNED')}>Unassigned</button>
            <button className={`tab ${activeTab === 'ALL' ? 'active' : ''}`} onClick={() => setActiveTab('ALL')}>All Tasks</button>
          </div>
          {activeTab === 'UNASSIGNED' && (unassignedTasks || []).length > 0 && (
            <button className="btn-primary btn-sm" onClick={bulkAssignToMe} disabled={bulkAssigning}>
              {bulkAssigning ? 'Assigning…' : 'Bulk Assign to Me'}
            </button>
          )}
        </div>

        <div className="card-body">
          <div className="task-filters">
            <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} className="filter-select">
              <option value="">All Check Types</option>
              {Object.values(CHECK_TYPES).map(type => (
                <option key={type} value={type}>{type.replace('_', ' ').toUpperCase()}</option>
              ))}
            </select>
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="filter-select">
              <option value="">All Statuses</option>
              {Object.values(VERIFICATION_STATUSES).map(status => (
                <option key={status} value={status}>{status.replace('_', ' ').toUpperCase()}</option>
              ))}
            </select>
          </div>

          <div className="desktop-view">
            <DataTable columns={columns} data={filteredTasks} loading={loading} emptyMessage="No tasks found." />
          </div>

          <div className="mobile-view">
            {filteredTasks.length > 0 ? (
              filteredTasks.map(task => (
                <div key={task.id} className="mobile-task-card" onClick={() => navigate(`/verification/${task.id}`)}>
                  <div className="mobile-task-header">
                    <strong>{task.applicant_name || 'Applicant'}</strong>
                    <StatusBadge status={task.status} />
                  </div>
                  <div className="mobile-task-details">
                    <span>{task.check_type.replace('_', ' ')}</span>
                    <span>App: {task.application_request_id}</span>
                  </div>
                </div>
              ))
            ) : (
              <p className="empty-state">No tasks found.</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default VerificationDashboard;
